/* eslint-disable no-console */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  useTheme,
  useMediaQuery,
  IconButton,
  Stack,
  Slide
} from '@mui/material';
import {
  ErrorOutline,
  CheckCircleOutline,
  Close as CloseIcon
} from '@mui/icons-material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { format } from 'date-fns';
import { TransitionProps } from '@mui/material/transitions';
import { CompletionDialogProps } from './types';

// Slide transition for mobile
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
* CompletionDialog - Responsive dialog for marking inspections as complete (no file upload)
*/
const CompletionDialog: React.FC<CompletionDialogProps> = ({
  open,
  onClose,
  inspection,
  onComplete,
  loading,
  reportIssue,
  inspectionNotCompleted
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [completionNotes, setCompletionNotes] = useState<string>('');
  const [selectedReason, setSelectedReason] = useState<string>('');

  // Predefined reasons for inspection not completed
  const incompleteReasons = [
    'Owner Not Available',
    'Site Not Ready',
    'Access Denied',
    'Safety Hazards Present',
    'Weather Conditions',
    'Rescheduled (By Owner/Inspector)',
    'Cancelled',
    'Other (with comments)'
  ];

  // Responsive configuration
  const getResponsiveConfig = () => ({
    dialogMaxWidth: isMobile ? false : 'md',
    dialogFullScreen: isMobile,
    contentPadding: isMobile ? 2 : 3,
    buttonSize: isMobile ? 'medium' : 'medium',
    textFieldRows: isMobile ? 3 : 4,
    titleFontSize: isMobile ? '1.1rem' : '1.25rem',
    bodyFontSize: isMobile ? '0.875rem' : '1rem'
  });

  const config = getResponsiveConfig();

  // Reset form when dialog opens with new inspection
  useEffect(() => {
    if (open && inspection) {
      setCompletionNotes('');
      setSelectedReason('');
    }
  }, [open, inspection]);

  // Handle reason selection for incomplete inspections
  const handleReasonChange = (event: SelectChangeEvent<string>) => {
    const reason = event.target.value;
    setSelectedReason(reason);

    // If it's not "Other (with comments)", set the reason as completion notes
    if (reason !== 'Other (with comments)') {
      setCompletionNotes(reason);
    } else {
      // Clear completion notes so user can enter custom comments
      setCompletionNotes('');
    }
  };

  // Handle form submission
  const handleSubmit = () => {
    // No attachments since file upload is removed
    onComplete(completionNotes, []);
  };

  if (!inspection) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth={!isMobile}
      fullScreen={config.dialogFullScreen}
      maxWidth={config.dialogMaxWidth as any}
      TransitionComponent={isMobile ? Transition : undefined}
      sx={{
        '& .MuiDialog-paper': {
          margin: { xs: 0, sm: 2 },
          width: { xs: '100%', sm: 'auto' },
          maxHeight: { xs: '100%', sm: '90vh' }
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: { xs: 2, sm: 3 },
        pb: { xs: 1, sm: 2 },
        fontSize: config.titleFontSize,
        fontWeight: 600
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          {reportIssue ?
            <ErrorOutline fontSize="small" sx={{ mr: 1 }} color="error" /> :
            (inspectionNotCompleted ?
              <WarningAmberIcon fontSize="small" color='warning' sx={{ mr: 1 }} /> :
              <CheckCircleOutline color='success' fontSize="small" sx={{ mr: 1 }} />
            )
          }
          <Typography
            variant="h6"
            component="span"
            sx={{
              fontSize: config.titleFontSize,
              fontWeight: 'inherit'
            }}
          >
            {reportIssue ? 'Report Issue' :
              (inspectionNotCompleted ? 'Inspection Not Completed' : 'Complete Inspection')}
          </Typography>
        </Box>

        {isMobile && (
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
            sx={{ ml: 1 }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent sx={{
        p: { xs: 2, sm: 3 },
        pt: { xs: 1, sm: 2 }
      }}>
        <Alert
          severity={inspectionNotCompleted ? "warning" : "info"}
          sx={{
            mb: { xs: 2, sm: 3 },
            '& .MuiAlert-message': {
              fontSize: config.bodyFontSize
            }
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontSize: config.bodyFontSize }}
          >
            {reportIssue ?
              `Please describe the issue identified during the inspection. Any photos or files you uploaded during the inspection will be included automatically.`
              : inspectionNotCompleted ?
                `Please select the reason why the inspection could not be completed and provide any additional details if necessary.`
                : `Please provide details about the inspection results. Any photos or files you uploaded during the inspection will be included automatically.`}
          </Typography>
        </Alert>

        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{
              fontSize: { xs: '1rem', sm: '1.1rem' },
              fontWeight: 600,
              mb: 1
            }}
          >
            Inspection Details:
          </Typography>
          <Stack spacing={0.5}>
            <Typography
              variant="body2"
              sx={{ fontSize: config.bodyFontSize }}
            >
              <strong>Application:</strong> {inspection.applicationNumber} - {inspection.applicationTitle}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: config.bodyFontSize }}
            >
              <strong>Type:</strong> {inspection.inspectionType}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: config.bodyFontSize }}
            >
              <strong>Date & Time:</strong> {format(new Date(inspection.inspectionDate), 'MMM dd, yyyy')} • {inspection.timeSlot}
            </Typography>
          </Stack>
        </Box>

        {/* Conditional rendering based on inspectionNotCompleted */}
        {inspectionNotCompleted ? (
          <>
            {/* Dropdown for incomplete reasons */}
            <FormControl
              fullWidth
              sx={{ mb: { xs: 2, sm: 3 } }}
              size={isMobile ? "medium" : "medium"}
            >
              <InputLabel
                id="reason-select-label"
                sx={{ fontSize: config.bodyFontSize }}
              >
                Reason for Incomplete Inspection
              </InputLabel>
              <Select
                labelId="reason-select-label"
                id="reason-select"
                value={selectedReason}
                label="Reason for Incomplete Inspection"
                onChange={handleReasonChange}
                sx={{
                  '& .MuiSelect-select': {
                    fontSize: config.bodyFontSize
                  }
                }}
              >
                {incompleteReasons.map((reason) => (
                  <MenuItem
                    key={reason}
                    value={reason}
                    sx={{ fontSize: config.bodyFontSize }}
                  >
                    {reason}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Show text field only if "Other (with comments)" is selected */}
            {selectedReason === 'Other (with comments)' && (
              <TextField
                label="Additional Comments"
                multiline
                rows={config.textFieldRows}
                fullWidth
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
                placeholder="Please provide details about why the inspection could not be completed..."
                sx={{
                  mb: { xs: 2, sm: 3 },
                  '& .MuiInputBase-input': {
                    fontSize: config.bodyFontSize
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: config.bodyFontSize
                  }
                }}
              />
            )}
          </>
        ) : (
          /* Regular completion notes field for completed inspections */
          <TextField
            label="Completion Notes"
            multiline
            rows={config.textFieldRows}
            fullWidth
            value={completionNotes}
            onChange={(e) => setCompletionNotes(e.target.value)}
            placeholder="Add details about the inspection results, any issues found, and requirements that have been met or need attention..."
            sx={{
              mb: { xs: 2, sm: 3 },
              '& .MuiInputBase-input': {
                fontSize: config.bodyFontSize
              },
              '& .MuiInputLabel-root': {
                fontSize: config.bodyFontSize
              }
            }}
          />
        )}

        {/* Information about automatically included files */}
        <Alert
          severity="info"
          sx={{
            '& .MuiAlert-message': {
              fontSize: config.bodyFontSize
            }
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontSize: config.bodyFontSize }}
          >
            All photos and files uploaded during the inspection will be automatically included with this completion report.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{
        px: { xs: 2, sm: 3 },
        pb: { xs: 2, sm: 3 },
        pt: { xs: 1, sm: 2 },
        flexDirection: { xs: 'column-reverse', sm: 'row' },
        gap: { xs: 1, sm: 1 },
        // Override MUI's default button spacing
        '& > :not(style) ~ :not(style)': {
          marginLeft: 0
        },
        '& .MuiButton-root': {
          justifyContent: 'center',
          margin: 0
        },
      }}>
        <Button
          onClick={onClose}
          variant="outlined"
          size={config.buttonSize as any}
          fullWidth={isMobile}
          sx={{
            fontSize: config.bodyFontSize,
            py: { xs: 1.25, sm: 1 },
            order: { xs: 2, sm: 0 }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color={reportIssue ? "error" : inspectionNotCompleted ? "warning" : "success"}
          disabled={loading || (inspectionNotCompleted ? !selectedReason : !completionNotes)}
          startIcon={loading ? <CircularProgress size={20} /> : null}
          size={config.buttonSize as any}
          fullWidth={isMobile}
          sx={{
            fontSize: config.bodyFontSize,
            py: { xs: 1.25, sm: 1 },
            order: { xs: 1, sm: 0 }
          }}
        >
          {loading ? 'Submitting...' :
            reportIssue ? 'Report Issue' :
              inspectionNotCompleted ? 'Submit Incomplete Status' :
                'Mark as Completed'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompletionDialog;